import React, { useEffect, useState } from "react";
import { isValidCustomerEmail } from "../../validation/customerEmail";
import { formatRubPrice, formatUsdtPrice } from "../../visoria/helpers";
import { checkoutOtherStore, fetchOtherStoreUiConfig } from "../../visoria/api";
import { saveRecentStoreOrder } from "../../visoria/order-storage";
import { getReferralPayloadForCheckout } from "../../visoria/referral-storage";
import { goToOrderAfterClick, openPaymentTab } from "../../visoria/payment-tab";
import CaptchaNotice from "../../visoria/components/CaptchaNotice";
import { sendWithCaptcha, useCaptcha } from "../../visoria/captcha";
import { NINTENDO_STORE_ID } from "./useNintendoCards";

let uiConfig = null;

function loadUiConfig() {
  if (!uiConfig) {
    uiConfig = fetchOtherStoreUiConfig().catch(() => {
      uiConfig = null;
      return {};
    });
  }
  return uiConfig;
}

/** Сумма в USDT; 0 — если хотя бы у одного номинала нет цены в USDT. */
export function planUsdt(plan) {
  if (plan.items.some((item) => !(item.priceCryptoUsdt > 0))) return 0;
  const total = plan.items.reduce(
    (sum, item) => sum + item.priceCryptoUsdt * item.quantity,
    0,
  );
  return Math.round(total * 100) / 100;
}

function orderPayload(plan) {
  const items = plan.items.map((item) => ({
    productId: item.id,
    quantity: item.quantity,
  }));
  return items.length === 1 ? items[0] : { items };
}

/** Оформление набора карт из плана pickCards одним заказом. */
function CardsCheckout({ plan, onPriceChanged, storeId = NINTENDO_STORE_ID }) {
  const [consent, setConsent] = useState(false);
  const [email, setEmail] = useState("");
  const [cryptoChecked, setCryptoChecked] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [waitingCaptcha, setWaitingCaptcha] = useState(false);
  const [paymentLink, setPaymentLink] = useState(null);
  const [error, setError] = useState("");
  const [config, setConfig] = useState({});

  useEffect(() => {
    loadUiConfig().then(setConfig);
  }, []);

  const usdt = planUsdt(plan);
  const crypto = cryptoChecked && usdt > 0;

  const planKey = `${plan.rub}:${plan.items.map((item) => `${item.id}x${item.quantity}`).join(",")}`;
  useEffect(() => {
    setPaymentLink(null);
  }, [planKey, crypto, email]);
  const hasEmail = isValidCustomerEmail(email);
  const canPay = consent && hasEmail && !submitting;
  const captchaEnabled = useCaptcha(consent);
  const total = crypto ? formatUsdtPrice(usdt) : formatRubPrice(plan.rub);
  const privacyUrl =
    config.privacyPolicyUrl || "https://trendy-eng-shop.ru/offer#privacy";
  const minutes = config.paymentLifetimeMinutes || 5;

  const pay = async () => {
    if (!canPay) return;
    setSubmitting(true);
    setError("");
    setPaymentLink(null);

    try {
      const payload = {
        ...orderPayload(plan),
        email: email.trim(),
        consent: true,
        paymentMethod: crypto ? "crypto" : "sbp",
        expectedAmountRub: crypto ? undefined : plan.rub,
        expectedAmountUsdt: crypto ? usdt : undefined,
        ...getReferralPayloadForCheckout(),
      };
      const result = await sendWithCaptcha(
        (captchaToken) =>
          checkoutOtherStore(storeId, captchaToken ? { ...payload, captchaToken } : payload),
        setWaitingCaptcha,
      );
      if (result.orderId) {
        saveRecentStoreOrder(storeId, result.orderId, result.accessToken);
      }
      const orderUrl = result.orderId
        ? `/store/order/${result.orderId}${
            result.accessToken ? `?token=${encodeURIComponent(result.accessToken)}` : ""
          }`
        : null;
      if (result.paymentUrl) {
        if (!openPaymentTab(result.paymentUrl)) {
          setPaymentLink({ url: result.paymentUrl, orderUrl });
          return;
        }
        if (orderUrl) window.location.href = orderUrl;
        return;
      }
      if (orderUrl) {
        window.location.href = orderUrl;
        return;
      }
      setError("Не удалось создать платёж");
    } catch (payError) {
      if (payError?.code === "PRICE_CHANGED") {
        await onPriceChanged?.();
        const next =
          crypto && payError.amountUsdt != null
            ? formatUsdtPrice(payError.amountUsdt)
            : formatRubPrice(payError.amountRub);
        setError(`${payError.message} Новая сумма: ${next}.`);
        return;
      }
      setError(payError?.message || "Не удалось оформить заказ. Попробуйте позже.");
    } finally {
      setSubmitting(false);
      setWaitingCaptcha(false);
    }
  };

  return (
    <div className="mt-3 space-y-3 rounded-lg bg-gray-50 p-3 text-sm dark:bg-[#222222]">
      <label className="flex cursor-pointer gap-2">
        <input
          type="checkbox"
          checked={consent}
          onChange={(e) => setConsent(e.target.checked)}
          className="mt-1 accent-indigo-600"
        />
        <span className="text-gray-700 dark:text-gray-300">
          Чтобы оформить заказ, нужно дать согласие{" "}
          <a
            href={privacyUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-indigo-600 underline dark:text-indigo-400"
          >
            на обработку персональных данных
          </a>
          . Я даю согласие.
        </span>
      </label>

      <label className="block">
        <span className="font-medium text-gray-700 dark:text-gray-200">
          Email для получения {plan.codes > 1 ? "кодов" : "кода"}
        </span>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          readOnly={!consent}
          placeholder="example@mail.ru"
          autoComplete="email"
          className={`mt-1 w-full rounded-md border border-gray-300 px-3 py-2 outline-none focus:border-indigo-500 dark:border-gray-600 ${
            consent
              ? "bg-white text-gray-900 dark:bg-[#2a2a2a] dark:text-white"
              : "bg-gray-100 text-gray-400 dark:bg-gray-800"
          }`}
        />
        {!consent ? (
          <span className="mt-1 block text-xs text-gray-400">Нажмите галочку выше</span>
        ) : null}
      </label>

      {error ? <p className="text-sm text-red-600 dark:text-red-400">{error}</p> : null}

      {paymentLink ? (
        <>
          <a
            href={paymentLink.url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => goToOrderAfterClick(paymentLink.orderUrl)}
            className={`block w-full rounded-md px-3 py-2.5 text-center text-lg font-semibold text-white transition ${
              crypto ? "bg-purple-600 hover:bg-purple-500" : "bg-green-600 hover:bg-green-500"
            }`}
          >
            Перейти к оплате
          </a>
          <p className="text-center text-xs text-gray-500 dark:text-gray-400">
            Счёт создан. Браузер не открыл вкладку сам — нажмите кнопку.
          </p>
        </>
      ) : (
        <button
          type="button"
          disabled={!canPay}
          onClick={pay}
          className={`w-full rounded-md px-3 py-2.5 text-lg font-semibold transition ${
            submitting
              ? crypto
                ? "bg-purple-600 text-white"
                : "bg-green-600 text-white"
              : canPay
                ? crypto
                  ? "bg-purple-600 text-white hover:bg-purple-500"
                  : "bg-green-600 text-white hover:bg-green-500"
                : "bg-gray-300 text-gray-500 dark:bg-gray-700 dark:text-gray-400"
          }`}
        >
          {submitting ? (
            <>
              <span className="pay-spinner" aria-hidden="true" />
              {waitingCaptcha ? "Проверка «Я не робот»…" : "Создаём счёт…"}
            </>
          ) : hasEmail ? (
            `Оплатить ${total}`
          ) : (
            total
          )}
        </button>
      )}

      <p className="text-center text-xs text-gray-500 dark:text-gray-400">
        После нажатия «Оплатить» страница оплаты{crypto ? " USDT" : ""} откроется в новой
        вкладке. У вас будет {minutes} мин. на оплату.
      </p>
      {captchaEnabled ? <CaptchaNotice /> : null}

      {usdt > 0 ? (
        <label className="flex cursor-pointer items-center justify-center gap-2">
          <input
            type="checkbox"
            checked={cryptoChecked}
            onChange={(e) => setCryptoChecked(e.target.checked)}
            className="accent-purple-600"
          />
          <span className="text-gray-700 dark:text-gray-300">
            Оплатить криптовалютой{" "}
            <span className="text-purple-600 dark:text-purple-400">
              ({formatUsdtPrice(usdt)})
            </span>
          </span>
        </label>
      ) : null}
    </div>
  );
}

export default CardsCheckout;
